import React, { useState, ReactNode, useEffect, useRef } from "react";
import { navigate, useStaticQuery, graphql, Link } from "gatsby";
import { cn, Icon, IconChevron, InlineText } from "../../lib/src";
import labPkg from "../../lab/package.json";
import libPkg from "../../lib/package.json";
import {
	SearchNavigation,
	SearchableNavigationItem,
} from "../../lab/src/components/navigation/search-navigation/search-navigation";
import {
	isButtonItem,
	isLinkItem,
} from "../../lab/src/components/navigation/side-navigation/side-navigation-utils";
import { Splitter } from "./docs-splitter";
import * as scss from "./docs-sidemenu.scss";

type MenuItem = {
	id: string;
	href: string;
	name: string;
	env?: string;
	type?: string;
	category?: string;
	menuOrder?: number;
	deprecated?: boolean;
};

type SitePageEdge = {
	node: {
		path: string;
	};
};

type FancyDocPageEdge = {
	node: {
		docPageMeta: {
			title?: string;
			category?: string;
			menuOrder?: number;
			deprecated?: boolean;
		};
	};
};

const TOP_LEVEL_KEY = "TOP_LEVEL_KEY";
const GROUP_ORDER = [
	"Design",
	"Develop",
	"Content",
	"Foundations",
	"Components",
	"Patterns",
	TOP_LEVEL_KEY,
];
function getGroupOrder(group: string) {
	const idx = GROUP_ORDER.indexOf(group);
	return idx === -1 ? GROUP_ORDER.length : idx;
}

function useAllPagePaths(): {
	node: { path: string; context?: { menuOrder?: number; deprecated?: boolean } };
}[] {
	const data = useStaticQuery(graphql`
		query SideMenuQuery {
			allSitePage {
				edges {
					node {
						path
					}
				}
			}
			allFancyDocPage {
				edges {
					node {
						docPageMeta {
							title
							category
							menuOrder
						}
					}
				}
			}
		}
	`);

	// Create a map from page titles to metadata
	const pageMetadata: Record<string, { menuOrder?: number; deprecated?: boolean }> = {};

	data.allFancyDocPage.edges.forEach((edge: FancyDocPageEdge) => {
		const node = edge.node;
		if (node.docPageMeta?.title) {
			pageMetadata[node.docPageMeta.title] = {
				menuOrder: node.docPageMeta.menuOrder,
				deprecated: node.docPageMeta.deprecated, // TODO: deprecated is not working – can't query with GraphQL
			};
		}
	});

	return data.allSitePage.edges.map((edge: SitePageEdge) => {
		const path = edge.node.path;
		const pathParts = path.split("/").filter(Boolean);
		const pageName = pathParts[pathParts.length - 1];

		// Look up metadata by page name
		const context = pageMetadata[pageName] || {};

		return {
			node: {
				path,
				context,
			},
		};
	});
}

export function StyleguideSideMenu(props: { pageUrl: string | undefined }): JSX.Element {
	const navRef = useRef<HTMLElement>(null);
	const pageUrl = props.pageUrl ?? "/";

	const allSitePage = useAllPagePaths();

	const records: Record<string, { menuOrder?: number; deprecated?: boolean } | undefined> =
		allSitePage
			.filter((item) => !item.node.path.includes("404") && item.node.path !== "/")
			.reduce(
				(list, item) => {
					list[item.node.path] = item.node.context || {};
					return list;
				},
				{} as Record<string, { menuOrder?: number; deprecated?: boolean } | undefined>
			);

	const links = Object.keys(records);

	const groups = links.reduce(
		(acc, link) => {
			const match = link.match(/^\/([^\/]+)\/.+/); // first segment, but only if multiple segments `/cat/page` = cat; `/page/` = null
			let group = (match && match[1]) ?? TOP_LEVEL_KEY;
			if (group == "lib" || group == "lab") {
				group = "Components";
			}
			(acc[group] ?? (acc[group] = [])).push(link);
			return acc;
		},
		{} as Record<string, string[]>
	);

	function compareMenuOrder(a: MenuItem, b: MenuItem): number {
		// Both have menuOrder: sort numerically
		if (a.menuOrder !== undefined && b.menuOrder !== undefined) {
			return (a.menuOrder as number) - (b.menuOrder as number);
		}
		// Only one has menuOrder, it comes first
		if (a.menuOrder !== undefined) {
			return -1;
		}
		if (b.menuOrder !== undefined) {
			return 1;
		}
		// Fallback to existing alphabetical sort
		return compareTypeCategoryName(a, b);
	}

	const menuItems = Object.entries(groups)
		.map(([group, links]) => {
			const items = links.map((href) => urlToMenuItem(href, records));
			// Only sort with compareMenuOrder if not "Components"
			const sortedItems =
				group === "Components" ? items.sort(compareTypeCategoryName) : items.sort(compareMenuOrder);

			// Place "Components overview" in the top of the Components group
			if (group === "Components") {
				const idx = sortedItems.findIndex((item) => item.id === "components-overview");
				if (idx > 0) {
					const [overview] = sortedItems.splice(idx, 1);
					sortedItems.unshift(overview);
				}
			}

			return [group, sortedItems] as const;
		})
		.sort(([a], [b]) => getGroupOrder(a) - getGroupOrder(b));

	const [navVisible, setNavVisible] = useState(true);

	// Transform the menuItems into a hierarchical structure suitable for search
	const searchableMenuItems: SearchableNavigationItem[] = [];
	menuItems.forEach(([group, items]) => {
		if (group === TOP_LEVEL_KEY) {
			searchableMenuItems.push(
				...items.map((i) => ({
					id: i.id,
					title: i.name,
					href: i.href,
				}))
			);
		} else {
			const categories = [
				...new Set(items.filter((i) => i.category && i.category !== group).map((i) => i.category!)),
			].sort();

			// Add overview items first
			const groupLevelItems = items.filter((i) => i.category === group || !i.category);

			const children = [
				...groupLevelItems.map((i) => ({
					id: i.id,
					title: i.name,
					href: i.href,
				})),
				...categories.map((c) => ({
					id: `subgroup-${searchableMenuItems.length}-${c}`,
					title: c,
					children: items
						.filter((i) => i.category === c)
						.map((i) => ({
							id: i.id,
							title: i.name,
							href: i.href,
						})),
				})),
			];

			searchableMenuItems.push({
				id: `group-${searchableMenuItems.length}}`,
				title: group,
				children,
			});
		}
	});

	const toggleNav = () => {
		if (matchMedia("(max-width: 35rem)").matches) {
			setNavVisible((prevNavVisible) => !prevNavVisible);
		}
	};

	return (
		<>
			<nav aria-label="Menu controls">
				<button
					className={scss.navToggle}
					onClick={toggleNav}
					aria-controls="nav"
					aria-expanded={navVisible ? "true" : "false"}
				>
					Menu
				</button>
			</nav>
			<nav
				id="nav"
				ref={navRef}
				aria-label="Primary navigation"
				className={cn(scss.nav, navVisible && scss.hidden)}
			>
				<div className={scss.fixerGuy}>
					<Link to="/">
						<div className={cn(scss.topGuy, scss.level1)}>
							<InlineText
								lineHeight="multi-line"
								size="xLarge"
								tone="neutralLight"
								emphasis="strong"
							>
								Fancy
							</InlineText>
							<InlineText lineHeight="multi-line" size="xLarge" tone="neutralLight">
								Design System
							</InlineText>
						</div>
					</Link>
					<div className={cn(scss.level1, scss.versionBadgeContainer)}>
						<Link to="/Develop/Lib changelog">
							<div className={cn(scss.versionBadge)}>
								<InlineText size="small" tone="neutralLight">
									FancyLib &nbsp;
									<InlineText size="small" tone="neutralLight" emphasis="strong">
										{libPkg.version}
									</InlineText>
								</InlineText>
							</div>
						</Link>
						<Link to="/Develop/Lab changelog">
							<div className={cn(scss.versionBadge)}>
								<InlineText size="small" tone="neutralLight">
									FancyLab &nbsp;
									<InlineText size="small" tone="neutralLight" emphasis="strong">
										{labPkg.version}
									</InlineText>
								</InlineText>
							</div>
						</Link>
					</div>
					<div className={cn(scss.searchContainer, scss.level1)}>
						<SearchNavigation
							hotkey="K"
							label="SEARCH"
							placeholder="Search Fancy documentation"
							items={searchableMenuItems}
							navigate={(item) => {
								if (isLinkItem(item)) {
									navigate(item.href);
								} else if (isButtonItem(item)) {
									item.onClick();
								} else {
									throw new Error(`Unhandled item ${item}`);
								}
							}}
							className="fancy-root" //we need to set fancyRoot styles since we portal out of the root
						/>
					</div>
					<MenuLink
						name="Home"
						href="/"
						id="home"
						className={scss.level1}
						pageUrl={pageUrl}
						onClick={toggleNav}
					/>
					{menuItems.map(([group, items]) =>
						group === TOP_LEVEL_KEY ? (
							items.map((item) => (
								<MenuLink
									key={item.href}
									{...item}
									className={scss.level1}
									pageUrl={pageUrl}
									onClick={toggleNav}
									deprecated={records[item.href]?.deprecated}
								/>
							))
						) : (
							<Expander
								key={group}
								title={group}
								containsCurrent={items.some((i) => compareUrls(i.href, pageUrl))}
							>
								{items.map((item, idx) => (
									<React.Fragment key={item.href}>
										{(idx === 0 || item.category !== items[idx - 1].category) && item.category && (
											<div className={cn(scss.menuItem, scss.level2)}>{item.category}</div>
										)}
										<MenuLink
											{...item}
											className={scss.level2}
											pageUrl={pageUrl}
											onClick={toggleNav}
											deprecated={records[item.href]?.deprecated}
										/>
									</React.Fragment>
								))}
							</Expander>
						)
					)}
				</div>
				<Splitter
					className={scss.splitter}
					onDrag={(e) => navRef.current!.style.setProperty("--sidemenu-size", e.pageX + "px")}
				/>
			</nav>
		</>
	);
}

const collator = new Intl.Collator("en", { sensitivity: "base" });
function compareTypeCategoryName(a: MenuItem, b: MenuItem): number {
	let t = 0;
	if (a.type && b.type) {
		t = collator.compare(a.type, b.type);
	}
	if (t === 0 && a.category && b.category) {
		t = collator.compare(a.category, b.category);
	}
	if (t === 0) {
		t = collator.compare(a.name, b.name);
	}
	return t;
}

function nameToId(name: string): string {
	return name.replace(/[^a-z0-9]/gi, "-").toLowerCase();
}

function toSentenceCase(str: string): string {
	if (!str) return "";
	return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function urlToMenuItem(
	href: string,
	records: Record<string, { menuOrder?: number } | undefined>
): MenuItem {
	const parts = href.split("/").filter(Boolean);
	const record = records[href];

	// Return menu item for guideline pages
	if (parts.length === 2) {
		const rawName = parts[1];
		const name = toSentenceCase(rawName);
		return {
			id: nameToId(name),
			href,
			name,
			menuOrder: record?.menuOrder,
		};
	}

	// Return menu item for components
	const [env, type, category, name] = parts;

	return {
		id: nameToId(name),
		href,
		env,
		type,
		category,
		name,
	};
}

function MenuLink(props: { pageUrl: string; className?: string; onClick?: () => void } & MenuItem) {
	return (
		<Link
			to={props.href}
			className={cn(
				scss.menuItem,
				scss.clickable,
				compareUrls(props.href, props.pageUrl) && scss.current,
				props.className
			)}
			onClick={props.onClick}
		>
			<div>{props.name}</div>
			<div className={scss.menuItemBadgeContainer}>
				{props.deprecated && <div className={cn(scss.badgy, scss.deprecatedBadge)}>Deprecated</div>}
				{props.env && (
					<div className={cn(scss.badgy, props.env === "lab" && scss.labBadge)}>{props.env}</div>
				)}
			</div>
		</Link>
	);
}

function Expander(props: { title: string; containsCurrent: boolean; children: ReactNode }) {
	const [open, setOpen] = useState(props.containsCurrent);
	const ref = useRef<HTMLDivElement>(null);
	const ownRender = useRef(false);

	const toggle = () => {
		setOpen((s) => !s);
		ownRender.current = true;
	};

	useEffect(() => {
		if (ownRender.current) {
			ownRender.current = false;
			return;
		}
		if (props.containsCurrent && !open) {
			setOpen(true); //if we are rendered by our parent and we contain the current selection, we open.
		}
	});

	return (
		<>
			<button
				className={cn(
					scss.expander,
					scss.menuItem,
					scss.clickable,
					scss.level1,
					!open && props.containsCurrent && scss.containsCurrent
				)}
				onClick={toggle}
			>
				{props.title}
				<Icon rotation={open ? "180" : "0"}>
					<IconChevron />
				</Icon>
			</button>
			<div
				ref={ref}
				style={{ maxHeight: open ? ref.current?.scrollHeight : 0 }}
				className={cn(scss.expanderList, open && scss.shown)}
			>
				{props.children}
			</div>
		</>
	);
}

function compareUrls(url1: string, url2: string) {
	const normalize = (url: string) => (url.endsWith("/") ? url.slice(0, -1) : url);
	return normalize(url1) === normalize(url2);
}
